<script setup>
import { computed } from "vue";

const props = defineProps({
  risk: { type: Object, required: true },
  editable: { type: Boolean, required: false },
});

const emit = defineEmits(["update:risk"]);

const missingFields = computed(() => {
  if (!props.risk?.missing_fields) return [];
  return props.risk.missing_fields.split(",").map((f) => f.trim()).filter(Boolean);
});

const updateField = (field, value) => {
  emit("update:risk", { ...(props.risk ?? {}), [field]: value });
};

const formatDateTimeLocal = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const directMatch = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
  if (directMatch) {
    return `${directMatch[1]}T${directMatch[2]}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";

  const pad = (part) => String(part).padStart(2, "0");
  const year = parsed.getFullYear();
  const month = pad(parsed.getMonth() + 1);
  const day = pad(parsed.getDate());
  const hour = pad(parsed.getHours());
  const minute = pad(parsed.getMinutes());
  return `${year}-${month}-${day}T${hour}:${minute}`;
};

const inputClass = "w-full p-2 rounded border bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100";
const textareaClass = inputClass;
</script>

<template>
  <FormBox header="Journey & Patient Details">
    <div class="space-y-4">
      <div v-if="missingFields.length" class="rounded border border-amber-200 bg-amber-50 text-amber-900 p-3 text-sm">
        Missing info: {{ missingFields.join(", ") }}
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200">Pickup</h3>
          <div>
            <label class="block text-xs text-slate-500">Pickup Location Name <span class="text-slate-400">(optional)</span></label>
            <input :class="inputClass" :value="risk.pickup_location ?? ''" :disabled="!editable"
              placeholder="e.g. Patient home or Ward 4"
              @input="updateField('pickup_location', $event.target.value)" />
          </div>
          <div>
            <label class="block text-xs text-slate-500">Pickup Address</label>
            <textarea :class="textareaClass" rows="3" :disabled="!editable"
              placeholder="Enter the full pickup address"
              :value="risk.pickup_address ?? ''" @input="updateField('pickup_address', $event.target.value)"></textarea>
          </div>
          <div>
            <label class="block text-xs text-slate-500">Pickup Time</label>
            <input :class="inputClass" type="datetime-local" :value="formatDateTimeLocal(risk.pickup_time)"
              :disabled="!editable" @input="updateField('pickup_time', $event.target.value)" />
          </div>
        </div>
        <div class="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200">Drop-off</h3>
          <div>
            <label class="block text-xs text-slate-500">Drop-off Location Name <span class="text-slate-400">(optional)</span></label>
            <input :class="inputClass" :value="risk.dropoff_location ?? ''" :disabled="!editable"
              placeholder="e.g. Outpatients or Ward 7"
              @input="updateField('dropoff_location', $event.target.value)" />
          </div>
          <div>
            <label class="block text-xs text-slate-500">Drop-off Address</label>
            <textarea :class="textareaClass" rows="3" :disabled="!editable"
              placeholder="Enter the full drop-off address"
              :value="risk.dropoff_address ?? ''" @input="updateField('dropoff_address', $event.target.value)"></textarea>
          </div>
          <div>
            <label class="block text-xs text-slate-500">Calculated hospital arrival</label>
            <input :class="inputClass" type="datetime-local" :value="formatDateTimeLocal(risk.return_time)"
              disabled />
          </div>
        </div>
      </div>

      <div>
        <label class="mb-3 flex items-center gap-2 text-sm">
          <input type="checkbox" :checked="Boolean(risk.return_required)" :disabled="!editable"
            @change="updateField('return_required', $event.target.checked)" />
          Wait and return to pickup location
        </label>
        <label for="pts-waiting-hours" class="block text-xs text-slate-500">Waiting hours <span class="text-slate-400">(optional)</span></label>
        <input id="pts-waiting-hours" :class="inputClass" type="number" min="0" max="9999.99" step="0.01"
          :value="risk.waiting_hours ?? ''" :disabled="!editable" placeholder="e.g. 1.5"
          @input="updateField('waiting_hours', $event.target.value === '' ? null : Number($event.target.value))" />
        <p class="mt-1 text-xs text-slate-500">
          Time waiting at the drop-off location after the calculated hospital arrival.
          <span v-if="risk.return_required">The journey then returns to the pickup location before returning to base. Waiting and return travel are included in pricing.</span>
        </p>
      </div>

      <div>
        <label class="block text-xs text-slate-500">Calculated finish at base</label>
        <input :class="inputClass" type="datetime-local" :value="formatDateTimeLocal(risk.calculated_finish_at)" disabled />
        <p class="mt-1 text-xs text-slate-500">Save to calculate arrival and finish from pickup, driving, waiting and return travel. Includes 15 minutes per started driving hour and 15 minutes after the final patient drop-off.</p>
      </div>

      <div class="border-t border-slate-200 pt-4 dark:border-slate-700">
        <h3 class="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">Patient Details</h3>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label class="block text-xs text-slate-500">Patient Name</label>
            <input :class="inputClass" :value="risk.patient_name ?? ''" :disabled="!editable"
              @input="updateField('patient_name', $event.target.value)" />
          </div>
          <div>
            <label class="block text-xs text-slate-500">Patient DOB</label>
            <input :class="inputClass" type="date" :value="risk.patient_dob ?? ''" :disabled="!editable"
              @input="updateField('patient_dob', $event.target.value)" />
          </div>
          <div>
            <label class="block text-xs text-slate-500">Mobility</label>
            <input :class="inputClass" :value="risk.mobility ?? ''" :disabled="!editable"
              @input="updateField('mobility', $event.target.value)" />
          </div>
        </div>
      </div>
      <div>
        <label class="block text-xs text-slate-500">Equipment</label>
        <textarea :class="textareaClass" rows="2" :disabled="!editable"
          :value="risk.equipment ?? ''" @input="updateField('equipment', $event.target.value)"></textarea>
      </div>
      <div>
        <label class="block text-xs text-slate-500">Clinical Notes</label>
        <textarea :class="textareaClass" rows="2" :disabled="!editable"
          :value="risk.clinical_notes ?? ''" @input="updateField('clinical_notes', $event.target.value)"></textarea>
      </div>
      <div>
        <label class="block text-xs text-slate-500">Risk Factors</label>
        <textarea :class="textareaClass" rows="2" :disabled="!editable"
          :value="risk.risk_factors ?? ''" @input="updateField('risk_factors', $event.target.value)"></textarea>
      </div>
    </div>
  </FormBox>
</template>
